import { useState, useEffect, useMemo } from 'react';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import ClearFiltersButton from '../components/common/ClearFiltersButton';
import AlphaFilter from '../components/common/AlphaFilter';
import { useAuth } from '../context/AuthContext';
import { useUrlParams } from '../hooks/useUrlState';
import Pagination from '../components/Pagination';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';

export default function PeoplePage() {
  const { user } = useAuth();
  const [people, setPeople] = useState(null);
  const [error, setError] = useState(false);
  const { get, set } = useUrlParams();
  const search = get('search', '');
  const alphaFilter = get('alpha', '');
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', role: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [detailId, setDetailId] = useState(null);

  function load() {
    setError(false);
    api.get('/people').then(r => setPeople(r.data)).catch(() => setError(true));
  }
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!people) return [];
    let list = people;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(s));
    }
    if (alphaFilter) {
      list = list.filter(p => (p.first_name || "").charAt(0).toUpperCase() === alphaFilter);
    }
    return list;
  }, [people, search, alphaFilter]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  async function handleSubmit() {
    if (!form.first_name || !form.last_name) {
      setFormError('First and last name are required');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await api.post('/people', form);
      setForm({ first_name: '', last_name: '', email: '', phone: '', role: '', notes: '' });
      setShowForm(false);
      load();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Could not save person');
    } finally {
      setSaving(false);
    }
  }

  async function handleExport() {
    await downloadCsv('/people/export', 'people-export.csv', exportIdsParam(filtered, people));
  }

  function fmt(n) {
    return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">People {people && <span className="page-title-count">({people.length} total)</span>}</h1>
          <p className="page-subtitle">Referral and commission beneficiaries. Owed = Earned − Paid − Expenses.</p>
        </div>
        <div className="page-actions">
          {user?.role === 'admin' && (
            <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
          )}
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
          <button className="btn btn-primary" onClick={() => setShowForm(s => !s)}>+ Add Person</button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
          {formError && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
              {formError}
            </div>
          )}
          <div className="form-grid">
            <div className="form-field"><label>First name</label><input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} /></div>
            <div className="form-field"><label>Last name</label><input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} /></div>
            <div className="form-field"><label>Email</label><input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div className="form-field"><label>Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
            <div className="form-field span-2"><label>Role / relationship</label><input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} placeholder="e.g. Referral partner, Internal sourcer" /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Add Person'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <input type="text" className="filter-search" placeholder="Search people…" value={search} onChange={e => set({ search: e.target.value, page: 1 })} />
        <ClearFiltersButton active={!!(search || alphaFilter)} onClear={() => set({ search: '', alpha: '', page: 1 })} />
      </div>
      <AlphaFilter items={people || []} nameKey="first_name" value={alphaFilter} onChange={l => set({ alpha: l, page: 1 })} />

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && people === null && <StateBlock type="loading" />}
      {!error && people !== null && filtered.length === 0 && <StateBlock type="noResults" />}

      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Role</th><th>WOs Linked</th><th>Earned</th><th>Paid</th><th>Expenses</th><th>Owed</th></tr>
            </thead>
            <tbody>
              {paged.map(p => (
                <tr key={p.id}>
                  <td><button className="row-name-link" onClick={() => setDetailId(p.id)}>{p.first_name} {p.last_name}</button></td>
                  <td>{p.role || '—'}</td>
                  <td>{p.wo_count}</td>
                  <td>{fmt(p.earned)}</td>
                  <td>{fmt(p.paid)}</td>
                  <td>{fmt(p.expenses)}</td>
                  <td style={{ fontWeight: 700, color: p.owed > 0 ? 'var(--success-text)' : p.owed < 0 ? 'var(--danger-text)' : undefined }}>{fmt(p.owed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={pageSize} total={filtered.length} onPageChange={(p) => set({ page: p })} onPageSizeChange={(s) => set({ pageSize: s, page: 1 })} />
        </>
      )}

      {detailId && <PersonDetailPanel personId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}

      {showImport && (
        <CsvImportModal entityLabel="Person" importPath="/people/import" onClose={() => setShowImport(false)} onComplete={load} />
      )}
    </div>
  );
}

function PersonDetailPanel({ personId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [showPayForm, setShowPayForm] = useState(false);
  const [payForm, setPayForm] = useState({ payment_date: '', amount: '', payment_type: 'commission_payout', notes: '' });
  const [saving, setSaving] = useState(false);

  function load() { api.get(`/people/${personId}`).then(r => setData(r.data)); }
  useEffect(load, [personId]);

  function fmt(n) { return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`; }

  async function addPayment() {
    setSaving(true);
    try {
      await api.post(`/people/${personId}/payments`, { ...payForm, amount: Number(payForm.amount) });
      setPayForm({ payment_date: '', amount: '', payment_type: 'commission_payout', notes: '' });
      setShowPayForm(false);
      load();
      onChanged();
    } finally { setSaving(false); }
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel">
        <div className="drawer-header">
          <h3 className="drawer-title">{data ? `${data.first_name} ${data.last_name}` : 'Loading…'}</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>
        {data && (
          <>
            <div className="panel-tabs">
              {['Overview', 'Work Orders', 'Ledger'].map(t => (
                <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>{t}</button>
              ))}
            </div>
            <div className="panel-body">
              {tab === 'Overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
                  <Field label="Email" value={data.email} />
                  <Field label="Phone" value={data.phone} />
                  <Field label="Role" value={data.role} />
                  <Field label="Notes" value={data.notes} />
                </div>
              )}
              {tab === 'Work Orders' && (
                data.wo_links.length === 0
                  ? <StateBlock type="empty" title="Not linked to any Work Orders" />
                  : data.wo_links.map(w => (
                    <div key={w.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: 600 }}>{w.client_wo_number}</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                        {w.contractor_name} · {w.client_name} · {w.commission_type === 'percentage' ? `${w.commission_value}%` : w.commission_type === 'fixed' ? `$${w.commission_value}/hr` : 'No commission set'}
                      </div>
                    </div>
                  ))
              )}
              {tab === 'Ledger' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                    <LedgerCard label="Earned" value={fmt(data.ledger.earned)} />
                    <LedgerCard label="Paid" value={fmt(data.ledger.paid)} />
                    <LedgerCard label="Expenses" value={fmt(data.ledger.expenses)} />
                    <LedgerCard label="Owed" value={fmt(data.ledger.owed)} highlight />
                  </div>
                  <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Payments</h4>
                  {data.payments.length === 0 && <div className="form-hint" style={{ marginBottom: 12 }}>No payments recorded.</div>}
                  {data.payments.map(p => (
                    <div key={p.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span>{p.payment_date} — {p.payment_type.replace('_', ' ')}</span>
                      <span>{fmt(p.amount)}</span>
                    </div>
                  ))}
                  {!showPayForm ? (
                    <button className="btn btn-secondary" style={{ width: '100%', marginTop: 14 }} onClick={() => setShowPayForm(true)}>+ Record Payout</button>
                  ) : (
                    <div style={{ marginTop: 14 }}>
                      <div className="form-field" style={{ marginBottom: 10 }}>
                        <label>Date</label>
                        <input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} />
                      </div>
                      <div className="form-field" style={{ marginBottom: 10 }}>
                        <label>Amount</label>
                        <input type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} />
                      </div>
                      <div className="form-field" style={{ marginBottom: 10 }}>
                        <label>Type</label>
                        <select value={payForm.payment_type} onChange={e => setPayForm(f => ({ ...f, payment_type: e.target.value }))}>
                          <option value="commission_payout">Commission payout</option>
                          <option value="advance">Advance</option>
                          <option value="draw">Draw</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowPayForm(false)}>Cancel</button>
                        <button className="btn btn-primary" style={{ flex: 1 }} onClick={addPayment} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Field({ label, value }) {
  return <div><div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div><div>{value || '—'}</div></div>;
}

function LedgerCard({ label, value, highlight }) {
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: highlight ? 'var(--primary)' : undefined }}>{value}</div>
    </div>
  );
}
